import { AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";

export function formatDateCell(value: string | null | undefined): string {
  if (!value) return "-";
  const d = new Date(value);
  return d.toLocaleString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }) + " UTC";
}

export function formatDuration(startedAtUtc: string, finishedAtUtc: string | null): string {
  if (!finishedAtUtc) return "-";
  const ms = new Date(finishedAtUtc).getTime() - new Date(startedAtUtc).getTime();
  if (ms < 0) return "-";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export const LoadingSpinner: React.FC<{ isDarkMode: boolean; message?: string }> = ({
  isDarkMode,
  message = "Loading...",
}) => (
  <div className="flex items-center justify-center h-48">
    <div className="flex flex-col items-center">
      <div
        className={`animate-spin rounded-full h-8 w-8 border-b-2 mb-3 ${
          isDarkMode ? "border-rp-amber" : "border-blue-600"
        }`}
      />
      <p className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>{message}</p>
    </div>
  </div>
);

export const ErrorBanner: React.FC<{
  message: string;
  onRetry: () => void;
  isDarkMode: boolean;
}> = ({ message, onRetry, isDarkMode }) => (
  <div
    className={`p-4 rounded-lg ${
      isDarkMode ? "bg-red-900/20 border border-red-800" : "bg-red-50 border border-red-200"
    }`}
  >
    <div className="flex items-center mb-3">
      <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
      <p className={`text-sm ${isDarkMode ? "text-red-400" : "text-red-700"}`}>{message}</p>
    </div>
    <button
      onClick={onRetry}
      className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
    >
      Retry
    </button>
  </div>
);

export const EmptyState: React.FC<{
  message: string;
  isDarkMode: boolean;
  icon?: React.ReactNode;
}> = ({ message, isDarkMode, icon }) => (
  <div className="flex items-center justify-center h-48">
    <div className="flex flex-col items-center">
      {icon}
      <p className={`text-sm ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`}>{message}</p>
    </div>
  </div>
);

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  isDarkMode: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  isDarkMode,
}) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between pt-4">
      <span className={`text-xs ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`}>
        {totalItems} total records
      </span>
      <div className="flex items-center space-x-2">
        <button
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          className={`p-1 rounded disabled:opacity-30 ${
            isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-200"
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className={`text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}>
          {currentPage} / {totalPages}
        </span>
        <button
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className={`p-1 rounded disabled:opacity-30 ${
            isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-200"
          }`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
